import { inject, Injectable, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { APP_VERSION } from '../../../environments/version';

const CHECK_INTERVAL_MS = 10 * 60 * 1000;
const TOAST_KEY = 'app-update';

/**
 * Detects when a newer build has been deployed while this tab is still open.
 *
 * `APP_VERSION` is baked into the JS bundle, so it only ever reflects what
 * was true when *this* tab last loaded — a running tab can't learn about a
 * newer build from it. `public/version.json` is shipped unbundled and served
 * with `Cache-Control: no-cache` (see nginx.conf) specifically so a running
 * tab can poll it and compare.
 *
 * On a mismatch this surfaces a sticky "Reload" toast (app.html, key
 * `app-update`) rather than reloading on its own — an unprompted reload
 * could drop whatever the user is mid-typing into a minute/discussion form.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private messageService = inject(MessageService);
  private tr = inject(TranslateService);

  readonly updateAvailable = signal(false);

  private started = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.started) return;
    this.started = true;

    this.check();
    this.timer = setInterval(() => this.check(), CHECK_INTERVAL_MS);

    // A long-lived background tab (dashboard left open overnight) gets its
    // timers throttled by the browser — check again the moment it's actually
    // looked at, so the toast isn't stale by the time it's seen.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.check();
    });
  }

  reload(): void {
    window.location.reload();
  }

  private async check(): Promise<void> {
    if (this.updateAvailable()) return;

    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!res.ok) return;

      const data: { version?: string } = await res.json();
      if (data.version && data.version !== APP_VERSION) {
        this.updateAvailable.set(true);
        this.messageService.add({
          key: TOAST_KEY,
          severity: 'info',
          sticky: true,
          summary: this.tr.instant('A new version of Tawtheiq is available.'),
          detail: this.tr.instant('Reload to get the latest updates.'),
        });
      }
    } catch {
      // Offline, or the deploy is mid-rollout — retried on the next tick.
    }
  }
}
